import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Typography,
  Tag,
  Tooltip,
  Popconfirm,
  message,
  Row,
  Col,
  Divider,
  Flex,
  Badge,
  Statistic,
} from "antd"
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
  DollarOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SettingOutlined,
  InfoCircleOutlined,
  PercentageOutlined,
  FileTextOutlined,
} from "@ant-design/icons"
import {
  getListQuarterTariff,
  upsertQuarterTariff,
  deleteQuarterTariff,
  resetQuarterTariffMessages,
} from "store/quarterTariffs/actions"
import { PersianDatePickerInline } from "components/Common/PersianDatePickerInline"

const { Title, Text } = Typography
const { Option } = Select

// Enum mappings
const QuarterTypeEnum = {
  1: { label: "بهار", color: "#52c41a" },
  2: { label: "تابستان", color: "#faad14" },
  3: { label: "پاییز", color: "#fa8c16" },
  4: { label: "زمستان", color: "#1890ff" },
}

const AllotmentRoundTypeEnum = {
  1: { label: "دور اول", color: "#1890ff" },
  2: { label: "دور دوم", color: "#722ed1" },
}

const PeriodEngGradeFactorEnum = {
  1: { label: "I", color: "#52c41a" },
  2: { label: "II", color: "#faad14" },
  3: { label: "III", color: "#ff4d4f" },
}

const QuarterTariffs = () => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingRecord, setEditingRecord] = useState(null)
  const [solarAllotmentDate, setSolarAllotmentDate] = useState("")

  const { lstQuarterTariff, loading, upsertLoading, deleteLoading, error, success } = useSelector(
    (state) => state.QuarterTariffs
  )

  // Fetch data on mount
  useEffect(() => {
    dispatch(getListQuarterTariff())
  }, [dispatch])

  // Handle success/error messages
  useEffect(() => {
    if (success) {
      message.success(success)
      setIsModalOpen(false)
      form.resetFields()
      setEditingRecord(null)
      dispatch(resetQuarterTariffMessages())
    }
    if (error) {
      message.error(error)
      dispatch(resetQuarterTariffMessages())
    }
  }, [success, error, dispatch, form])

  // Open modal for create
  const handleCreate = () => {
    setEditingRecord(null)
    form.resetFields()
    
    // Get latest period from existing records
    const latestPeriod = lstQuarterTariff?.length > 0 
      ? Math.max(...lstQuarterTariff.map(item => item.period || 0))
      : 0
    
    form.setFieldsValue({
      year: 1405,
      quarterTypeEnum: 1,
      allotmentRoundTypeEnum: 1,
      period: latestPeriod + 1,
      fee: 0,
      isNewGradeBase: true,
      isDeleteFactor: true,
      cancelBeforeAcceptBurnPercent: 10,
      cancelAfterAcceptBurnPercent: 0,
      cancelByEngBurnPercent: 0,
      includeCancelBurn: true,
    })
    setIsModalOpen(true)
  }

  // Open modal for edit
  const handleEdit = (record) => {
    setEditingRecord(record)
    form.setFieldsValue({
      ...record,
      // Convert date string to appropriate format if needed
    })
    setIsModalOpen(true)
  }

  // Handle delete
  const handleDelete = (id) => {
    dispatch(deleteQuarterTariff(id))
  }

  // Handle form submit
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      const payload = {
        ...values,
        solarAllotmentDate: solarAllotmentDate?.persian,
        julianAllotmentDate: solarAllotmentDate?.gregorian,
        ...(editingRecord?.id && { id: editingRecord.id }),
      }
      dispatch(upsertQuarterTariff(payload))
    } catch (error) {
      console.error("Validation failed:", error)
    }
  }

  // Handle refresh
  const handleRefresh = () => {
    dispatch(getListQuarterTariff())
  }

  // Format number with thousand separator
  const formatNumber = (num) => {
    if (num === null || num === undefined) return "0"
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  }

  // Table columns
  const columns = [
    {
      title: (
        <Space>
          <CalendarOutlined />
          سال
        </Space>
      ),
      dataIndex: "year",
      key: "year",
      width: 80,
      align: "center",
      sorter: (a, b) => a.year - b.year,
      render: (year) => <Text strong>{year}</Text>,
    },
    {
      title: "فصل",
      dataIndex: "quarterTypeEnum",
      key: "quarterTypeEnum",
      width: 100,
      align: "center",
      filters: Object.entries(QuarterTypeEnum).map(([key, val]) => ({
        text: val.label,
        value: parseInt(key),
      })),
      onFilter: (value, record) => record.quarterTypeEnum === value,
      render: (type, record) => {
        const quarterInfo = QuarterTypeEnum[type] || { label: "-", color: "#999" }
        return (
          <Tag color={quarterInfo.color} style={{ minWidth: 50 }}>
            {record.quarterTypeName || quarterInfo.label}
          </Tag>
        )
      },
    },
    {
      title: "دور",
      dataIndex: "allotmentRoundTypeEnum",
      key: "allotmentRoundTypeEnum",
      width: 90,
      align: "center",
      filters: Object.entries(AllotmentRoundTypeEnum).map(([key, val]) => ({
        text: val.label,
        value: parseInt(key),
      })),
      onFilter: (value, record) => record.allotmentRoundTypeEnum === value,
      render: (type, record) => {
        const roundInfo = AllotmentRoundTypeEnum[type] || { label: "-", color: "#999" }
        return (
          <Tag color={roundInfo.color}>
            {record.allotmentRoundTypeName || roundInfo.label}
          </Tag>
        )
      },
    },
    {
      title: "دوره",
      dataIndex: "period",
      key: "period",
      width: 70,
      align: "center",
      render: (period) => (
        <Badge count={period} style={{ backgroundColor: "#1890ff" }} />
      ),
    },
    {
      title: (
        <Space>
          <DollarOutlined />
          تعرفه (ریال)
        </Space>
      ),
      dataIndex: "fee",
      key: "fee",
      width: 140,
      align: "left",
      sorter: (a, b) => a.fee - b.fee,
      render: (fee) => (
        <Text strong style={{ color: "#52c41a" }}>
          {formatNumber(fee)}
        </Text>
      ),
    },
    {
      title: "تاریخ تخصیص",
      dataIndex: "solarAllotmentDate",
      key: "solarAllotmentDate",
      width: 120,
      align: "center",
      render: (date) => (
        <Text type="secondary">{date || "-"}</Text>
      ),
    },
    {
      title: "وضعیت سهمیه",
      dataIndex: "isQuota",
      key: "isQuota",
      width: 100,
      align: "center",
      filters: [
        { text: "تایید شده", value: true },
        { text: "تایید نشده", value: false },
      ],
      onFilter: (value, record) => record.isQuota === value,
      render: (isQuota) =>
        isQuota ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            تایید شده
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="default">
            تایید نشده
          </Tag>
        ),
    },
    {
      title: (
        <Space>
          <PercentageOutlined />
          درصد سوزاندن
        </Space>
      ),
      key: "burnPercents",
      width: 180,
      align: "center",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 11 }}>
            قبل تایید: <Text strong>{record.cancelBeforeAcceptBurnPercent}%</Text>
          </Text>
          <Text style={{ fontSize: 11 }}>
            بعد تایید: <Text strong>{record.cancelAfterAcceptBurnPercent}%</Text>
          </Text>
          <Text style={{ fontSize: 11 }}>
            توسط مهندس: <Text strong>{record.cancelByEngBurnPercent}%</Text>
          </Text>
        </Space>
      ),
    },
    {
      title: "عملیات",
      key: "actions",
      width: 120,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Tooltip title={record.isQuota ? "امکان ویرایش وجود ندارد" : "ویرایش"}>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              disabled={record.isQuota}
              style={{ color: record.isQuota ? "#d9d9d9" : "#1890ff" }}
            />
          </Tooltip>
          <Popconfirm
            title="حذف تخصیص"
            description="آیا از حذف این تخصیص اطمینان دارید؟"
            onConfirm={() => handleDelete(record.id)}
            okText="بله"
            cancelText="خیر"
            disabled={record.isQuota}
          >
            <Tooltip title={record.isQuota ? "امکان حذف وجود ندارد" : "حذف"}>
              <Button
                type="text"
                icon={<DeleteOutlined />}
                danger
                disabled={record.isQuota}
                loading={deleteLoading}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  // Calculate summary stats
  const totalRecords = lstQuarterTariff?.length || 0
  const confirmedRecords = lstQuarterTariff?.filter((r) => r.isQuota).length || 0

  return (
    <div style={{ padding: "0" }}>
      {/* Header Card */}
      <Card
        style={{
          marginBottom: 16,
          borderRadius: 12,
          border: "none",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
        styles={{ body: { padding: 0 } }}
      >
        <div
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: "12px 12px 0 0",
            padding: "20px 24px",
          }}
        >
          <Flex justify="space-between" align="center" wrap="wrap" gap={16}>
            <Flex align="center" gap={12}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  background: "rgba(255,255,255,0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <SettingOutlined style={{ fontSize: 24, color: "#fff" }} />
              </div>
              <div>
                <Title level={4} style={{ margin: 0, color: "#fff" }}>
                  مدیریت تخصیص‌های سه‌ماهه
                </Title>
                <Text style={{ color: "rgba(255,255,255,0.8)" }}>
                  تنظیم تعرفه‌ها و دوره‌های تخصیص مهندسین
                </Text>
              </div>
            </Flex>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                loading={loading}
              >
                بروزرسانی
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
                style={{
                  background: "rgba(255,255,255,0.2)",
                  borderColor: "transparent",
                }}
              >
                تخصیص جدید
              </Button>
            </Space>
          </Flex>
        </div>

        {/* Stats Row */}
        <div style={{ padding: "16px 24px", background: "#fafafa" }}>
          <Row gutter={24}>
            <Col xs={12} sm={8} md={6}>
              <Statistic
                title="کل تخصیص‌ها"
                value={totalRecords}
                prefix={<FileTextOutlined style={{ color: "#1890ff" }} />}
                valueStyle={{ color: "#1890ff" }}
              />
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Statistic
                title="تایید شده"
                value={confirmedRecords}
                prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
                valueStyle={{ color: "#52c41a" }}
              />
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Statistic
                title="در انتظار"
                value={totalRecords - confirmedRecords}
                prefix={<CloseCircleOutlined style={{ color: "#faad14" }} />}
                valueStyle={{ color: "#faad14" }}
              />
            </Col>
          </Row>
        </div>
      </Card>

      {/* Table Card */}
      <Card
        style={{
          borderRadius: 12,
          border: "none",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
        styles={{ body: { padding: 0 } }}
      >
        <Table
          columns={columns}
          dataSource={lstQuarterTariff}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} از ${total} مورد`,
          }}
          scroll={{ x: 1100 }}
          style={{ padding: 16 }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={
          <Flex align="center" gap={8}>
            {editingRecord ? <EditOutlined /> : <PlusOutlined />}
            <span>{editingRecord ? "ویرایش تخصیص" : "تخصیص جدید"}</span>
          </Flex>
        }
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false)
          setEditingRecord(null)
          form.resetFields()
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsModalOpen(false)
              setEditingRecord(null)
              form.resetFields()
            }}
          >
            انصراف
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={upsertLoading}
            onClick={handleSubmit}
          >
            {editingRecord ? "بروزرسانی" : "ثبت"}
          </Button>,
        ]}
        width={800}
        centered
      >
        <Divider />
        <Form
          form={form}
          layout="vertical"
          requiredMark="optional"
        >
          <Row gutter={16}>
            {/* Basic Info Section */}
            <Col span={24}>
              <Text strong style={{ color: "#1890ff", marginBottom: 16, display: "block" }}>
                <CalendarOutlined style={{ marginLeft: 8 }} />
                اطلاعات دوره
              </Text>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="year"
                label="سال"
                rules={[
                  { required: true, message: "سال الزامی است" },
                  { type: "number", min: 1401, message: "سال باید بزرگتر از 1400 باشد" },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="1403"
                  min={1401}
                  max={1500}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="quarterTypeEnum"
                label="فصل"
                rules={[{ required: true, message: "فصل الزامی است" }]}
              >
                <Select placeholder="انتخاب فصل">
                  {Object.entries(QuarterTypeEnum).map(([key, val]) => (
                    <Option key={key} value={parseInt(key)}>
                      <Tag color={val.color} style={{ marginLeft: 8 }}>
                        {val.label}
                      </Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="allotmentRoundTypeEnum"
                label="دور تخصیص"
                rules={[{ required: true, message: "دور الزامی است" }]}
              >
                <Select placeholder="انتخاب دور">
                  {Object.entries(AllotmentRoundTypeEnum).map(([key, val]) => (
                    <Option key={key} value={parseInt(key)}>
                      <Tag color={val.color}>{val.label}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="period"
                label="دوره"
                rules={[
                  { required: true, message: "دوره الزامی است" },
                  { type: "number", min: 1, message: "دوره باید بزرگتر از صفر باشد" },
                ]}
              >
                <InputNumber disabled style={{ width: "100%" }} min={1} />
              </Form.Item>
            </Col>

            {/* Financial Info */}
            <Col span={24} style={{ marginTop: 16 }}>
              <Text strong style={{ color: "#52c41a", marginBottom: 16, display: "block" }}>
                <DollarOutlined style={{ marginLeft: 8 }} />
                اطلاعات مالی
              </Text>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                name="fee"
                label="تعرفه (ریال)"
                rules={[
                  { required: true, message: "تعرفه الزامی است" },
                  { type: "number", min: 0, message: "تعرفه نمی‌تواند منفی باشد" },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value.replace(/,/g, "")}
                  min={0}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                name="julianAllotmentDate"
                label="تاریخ تخصیص"
                rules={[{ required: true, message: "تاریخ الزامی است" }]}
              >
                <PersianDatePickerInline
                  defaultDate={form.getFieldValue("julianAllotmentDate")?.toString()?.substring(0, 10)}
                  setPersianDate={(data) => {
                    setSolarAllotmentDate(data)
                    form.setFieldsValue({ julianAllotmentDate: data?.gregorian })
                  }}
                />
              </Form.Item>
            </Col>

            {/* <Col xs={24} sm={12}>
              <Form.Item
                name="periodEngGradeFactorEnum"
                label="فاکتور رتبه مهندس"
              >
                <Select placeholder="انتخاب فاکتور">
                  {Object.entries(PeriodEngGradeFactorEnum).map(([key, val]) => (
                    <Option key={key} value={parseInt(key)}>
                      <Tag color={val.color}>{val.label}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col> */}

            {/* Burn Percentages */}
            <Col span={24} style={{ marginTop: 16 }}>
              <Text strong style={{ color: "#fa8c16", marginBottom: 16, display: "block" }}>
                <PercentageOutlined style={{ marginLeft: 8 }} />
                درصدهای سوزاندن
              </Text>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                name="cancelBeforeAcceptBurnPercent"
                label="قبل از تایید (%)"
                rules={[
                  { type: "number", min: 0, max: 100, message: "باید بین 0 تا 100 باشد" },
                ]}
              >
                <InputNumber style={{ width: "100%" }} min={0} max={100} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                name="cancelAfterAcceptBurnPercent"
                label="بعد از تایید (%)"
                rules={[
                  { type: "number", min: 0, max: 100, message: "باید بین 0 تا 100 باشد" },
                ]}
              >
                <InputNumber style={{ width: "100%" }} min={0} max={100} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                name="cancelByEngBurnPercent"
                label="توسط مهندس (%)"
                rules={[
                  { type: "number", min: 0, max: 100, message: "باید بین 0 تا 100 باشد" },
                ]}
              >
                <InputNumber style={{ width: "100%" }} min={0} max={100} />
              </Form.Item>
            </Col>

            {/* Switches */}
            <Col span={24} style={{ marginTop: 16 }}>
              <Text strong style={{ color: "#722ed1", marginBottom: 16, display: "block" }}>
                <SettingOutlined style={{ marginLeft: 8 }} />
                تنظیمات پیشرفته
              </Text>
            </Col>

            <Col xs={12} sm={6}>
              <Form.Item
                name="isNewGradeBase"
                label="پایه رتبه جدید"
                valuePropName="checked"
              >
                <Switch checkedChildren="بله" unCheckedChildren="خیر" />
              </Form.Item>
            </Col>

            <Col xs={12} sm={6}>
              <Form.Item
                name="isDeleteFactor"
                label="حذف فاکتور"
                valuePropName="checked"
              >
                <Switch checkedChildren="بله" unCheckedChildren="خیر" />
              </Form.Item>
            </Col>

            <Col xs={12} sm={6}>
              <Form.Item
                name="includeCancelBurn"
                label="شامل سوزاندن"
                valuePropName="checked"
              >
                <Switch checkedChildren="بله" unCheckedChildren="خیر" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  )
}

export default QuarterTariffs
